import { useCallback, useEffect, useRef } from 'react';
import {
  MessageHeader,
  MsgTypeSubscriptionResponse,
  MsgTypeMarketConfigUpdate,
  MsgTypeMarketStateUpdate,
  MarketConfigUpdate,
  MarketStateUpdate,
  MsgTypeCandlesSnapshot,
  MsgTypeCandlesUpdate,
  CandleSeries,
  SubscriptionResponse,
  MsgTypeGasPriceUpdate,
  GasPriceUpdate,
  SubscriptionStatus,
  MsgTypeL2BookSnapshot,
  MsgTypeL2BookUpdate,
  L2Book,
  L2PriceLevel,
  MsgTypeTradesSnapshot,
  MsgTypeTradesUpdate,
  TradeSeries,
  MsgTypeMarketFundingUpdate,
  MarketFundingUpdate,
  MsgTypeHeartbeat,
  Heartbeat,
} from '@/api/spec.ts';
import useAppStore, { useSelectedMarketId } from '../../store/rootStore.ts';
import { useWebSocket, WS_ENDPOINTS, CommonWebSocketReturn, WebSocketManager } from '../../components/websocket';
import { useChainStore } from '../../store/chainStore.ts';
import { useOrderBookStore } from '../../features/order_book/state/orderBookStore.ts';
import { useLastTradesStore } from '../../features/order_book/state/lastTradesStore.ts';
import { useMarketDataSubscriptions } from './useMarketDataSubscriptions.ts';
import { useDexOrderBookSubscriptions } from '../../features/order_book/hooks/useDexOrderBookSubscriptions.ts';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils.ts';
import { NormalizedPriceLevel } from '../../features/order_book/state/types.ts';
import { useDexCandlesSubscriptions } from '../../features/tv_charts/candle_dex_source/useDexCandlesSubscriptions.ts';
import { DexCandlesDataFeed } from '../../features/tv_charts/candle_dex_source/DexCandlesDataFeed.ts';
import { useHeartbeatStore } from '@/common/heartbeatStore.ts';
import { DexIntervalMapper } from '../../features/tv_charts/candle_dex_source/DexIntervalMapper.ts';
import { useCandlestickChartStore } from '../../features/tv_charts/CandlestickChartStore.ts';

const intervalMapper = new DexIntervalMapper();
let subscriptions: Record<string, SubscriptionStatus> = {};

function mapL2PriceToInternal(level: L2PriceLevel, quoteDecimals: number, assetDecimals: number): NormalizedPriceLevel {
  const price = offsetNumberToNumber(level.p, quoteDecimals);
  const size = offsetNumberToNumber(level.s, assetDecimals);
  return {
    priceStr: price.toString(),
    price,
    size,
    count: level.o,
    product: price * size,
  };
}

export function useMarketWebSocket(): CommonWebSocketReturn {
  const chainId = useAppStore((state) => state.config?.chain.id);
  const canConnect = chainId != null;
  const selectedMarketId = useSelectedMarketId();
  const isOrderBookEnabled = selectedMarketId != null;
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasConnectedRef = useRef(false);

  const handleMessage = useCallback(
    (message: MessageHeader) => {
      const selectedMarketId = useAppStore.getState().selectedMarketId;

      const isMessageValid = (streamName: string) => {
        if (!selectedMarketId) return false;
        const currentSub = subscriptions[streamName];
        return !!(currentSub?.sid && message.sid === currentSub.sid);
      };

      switch (message.mt) {
        case MsgTypeSubscriptionResponse: {
          (message as SubscriptionResponse)?.subs.forEach((s) => {
            subscriptions[s.stream] = s;
          });
          break;
        }
        case MsgTypeHeartbeat: {
          const heartbeat = message as Heartbeat;
          const heartbeatStore = useHeartbeatStore.getState();
          const lastSn = heartbeatStore.marketData.lastSequenceNumber;

          if (heartbeat.sn == null || heartbeat.h == null) {
            console.warn('Received heartbeat with missing sn or h fields', heartbeat);
            return;
          }

          // Check if sequence number is strictly +1
          if (lastSn != null && heartbeat.sn !== lastSn + 1) {
            console.warn(
              `Market data heartbeat sequence mismatch: expected ${lastSn + 1}, got ${heartbeat.sn}. Reconnecting...`
            );
            WebSocketManager.getInstance().forceReconnect(WS_ENDPOINTS.MARKET_DATA);
            return;
          }

          heartbeatStore.updateMarketDataHeartbeat(heartbeat.sn, heartbeat.h);
          break;
        }
        case MsgTypeGasPriceUpdate: {
          const updateResult = useChainStore.getState().trySetLatest(message as GasPriceUpdate);
          if (!updateResult) {
            console.log('Chain state ahead of message', message);
          }
          break;
        }
        case MsgTypeMarketConfigUpdate: {
          const config = message as MarketConfigUpdate;
          for (const marketIdStr in config.d) {
            const marketId = Number(marketIdStr);
            const configData = config.d[marketId];
            useAppStore.getState().updateMarketConfig(marketId, configData);
          }
          break;
        }
        case MsgTypeMarketStateUpdate: {
          const stats = message as MarketStateUpdate;
          useAppStore.getState().updateMarketsStats(stats.d);
          break;
        }
        case MsgTypeMarketFundingUpdate: {
          const funding = message as MarketFundingUpdate;
          useAppStore.getState().updateMarketsFunding(funding.d);
          break;
        }
        case MsgTypeCandlesSnapshot:
        case MsgTypeCandlesUpdate: {
          if (selectedMarketId != null) {
            const candleSeries = message as CandleSeries;
            const currentResolution = useCandlestickChartStore.getState().resolution;
            const messageResolution = intervalMapper.getResolutionFromInterval(candleSeries.r.toString());

            if (currentResolution && messageResolution !== currentResolution) {
              break;
            }

            // Candle messages carry no market id — the sid is the only thing tying one to a
            // market, and the handlers below stamp whatever market is selected NOW. Without
            // this check an in-flight candle from the outgoing market arriving right after a
            // switch is forwarded to the new market's chart at the old market's price (the
            // long-lived widget re-subscribes via setSymbol fast enough to make that real).
            // The stream name matches useDexCandlesSubscriptions: `r` is the interval in
            // seconds, same value getExchangeInterval produced for the subscribe request.
            if (!isMessageValid(`candles@${selectedMarketId}*${candleSeries.r}`)) {
              break;
            }

            if (message.mt === MsgTypeCandlesSnapshot) {
              DexCandlesDataFeed.handleCandleSnapshot(selectedMarketId.toString(), candleSeries);
            } else {
              DexCandlesDataFeed.handleCandleUpdate(selectedMarketId.toString(), candleSeries);
            }
          }
          break;
        }
        case MsgTypeL2BookSnapshot:
        case MsgTypeL2BookUpdate: {
          if (!isMessageValid(`order-book@${selectedMarketId}`)) {
            return;
          }

          const orderBookStore = useOrderBookStore.getState();
          const { quoteDecimals, assetDecimals } = orderBookStore;

          if (message.mt === MsgTypeL2BookSnapshot) {
            const snapshot = message as L2Book;
            orderBookStore.setSnapshot({
              bids: snapshot.bid.map((pl) => mapL2PriceToInternal(pl, quoteDecimals, assetDecimals)),
              asks: snapshot.ask.map((pl) => mapL2PriceToInternal(pl, quoteDecimals, assetDecimals)),
              lastUpdateId: snapshot.at.b || 0,
            });
          } else {
            const update = message as L2Book;
            const blockNumber = update.at.b || 0;
            orderBookStore.updateOrderBook({
              U: blockNumber,
              u: blockNumber,
              b: update.bid.map((pl) => mapL2PriceToInternal(pl, quoteDecimals, assetDecimals)),
              a: update.ask.map((pl) => mapL2PriceToInternal(pl, quoteDecimals, assetDecimals)),
            });
          }
          break;
        }
        case MsgTypeTradesSnapshot:
        case MsgTypeTradesUpdate: {
          if (!isMessageValid(`trades@${selectedMarketId}`)) {
            return;
          }

          if (message.mt === MsgTypeTradesSnapshot) {
            const snapshot = message as TradeSeries;
            useLastTradesStore.getState().setSnapshot({
              trades: snapshot.d,
              lastUpdateId: snapshot.sn || 0,
            });
          } else {
            const update = message as TradeSeries;
            useLastTradesStore.getState().updateTrades(update);
          }
          break;
        }
        default: {
          console.log('Unhandled message', message);
        }
      }
    },
    [chainId]
  );

  const connection = useWebSocket({
    endpoint: WS_ENDPOINTS.MARKET_DATA,
    canConnect,
    onMessage: handleMessage,
  });

  // On WS reconnect (heartbeat-timeout forceReconnect, tab background/resume, network blip),
  // the backend re-sends a candle snapshot that we replay as realtime appends — but TradingView
  // never rebuilds a contiguous history, so gaps accumulate over a session and render as price
  // "jumps". Fire the datafeed reset so TradingView discards its cached bars and re-runs getBars
  // (REST backfill), reconstructing a gapless series. No-op when no chart is mounted.
  useEffect(() => {
    if (connection.status === 'OPEN') {
      if (wasConnectedRef.current) {
        DexCandlesDataFeed.onReconnect();
      }
      wasConnectedRef.current = true;
    }
  }, [connection.status]);

  useMarketDataSubscriptions({
    chainId,
    enabled: connection.status === 'OPEN' && !!chainId,
  });
  useDexOrderBookSubscriptions({
    symbol: selectedMarketId?.toString(),
    enabled: isOrderBookEnabled && connection.status === 'OPEN',
  });
  useDexCandlesSubscriptions({
    symbol: selectedMarketId?.toString(),
    enabled: isOrderBookEnabled && connection.status === 'OPEN',
  });

  // Monitor heartbeat timeout
  useEffect(() => {
    if (connection.status === 'OPEN') {
      heartbeatTimeoutRef.current = setInterval(() => {
        const health = useHeartbeatStore.getState().checkTimeout('marketData');

        if (health === 'error') {
          WebSocketManager.getInstance().forceReconnect(WS_ENDPOINTS.MARKET_DATA);
        }
      }, 1000);
    } else {
      if (heartbeatTimeoutRef.current) {
        clearInterval(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = null;
      }
      useHeartbeatStore.getState().resetMarketData();
    }

    return () => {
      if (heartbeatTimeoutRef.current) {
        clearInterval(heartbeatTimeoutRef.current);
      }
    };
  }, [connection.status]);

  return connection;
}
